import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  Button,
  Snackbar,
  Grid,
  Divider,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

function StudentResult() {
  const [results, setResults] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [studentResults, setStudentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(undefined);
  const [studentLoading, setStudentLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [sgpa, setSgpa] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [downloading, setDownloading] = useState(false);

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  function getMonthName(month) {
    const months = [
      "",
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];
    return months[month] || month;
  }

  // Helper to check if a schedule matches any result row
  function isScheduleInResults(schedule) {
    const detail = schedule.exam_schedule_details?.[0] || {};
    if (!detail) return false;
    return studentResults.some(
      (r) =>
        String(r.exam_schedule_detail_id) === String(detail.exam_schedule_detail_id) &&
        String(r.year_number) === String(detail.year_number) &&
        String(r.semester_number) === String(detail.semester_number)
    );
  }

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 80,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "year_number",
      headerName: "StudyYear",
      width: 110,
      valueGetter: (params) => params.row.exam_schedule_details?.[0]?.year_number || "",
    },
    {
      field: "semester_number",
      headerName: "Semester",
      width: 110,
      valueGetter: (params) => params.row.exam_schedule_details?.[0]?.semester_number || "",
    },
    {
      field: "exam_type_name",
      headerName: "ExamType",
      width: 120,
      valueGetter: (params) => params.row.exam_type.exam_type_name || "",
    },
    {
      field: "month_year",
      headerName: "MonthYear",
      width: 180,
      valueGetter: (params) =>
        params.row.exam_month && params.row.exam_year ? `${params.row.exam_month}` : "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const disabled = !isScheduleInResults(params.row);
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleShowResult(params.row)}
            sx={{ color: "#fff" }}
            disabled={disabled}
          >
            Show Result
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (!user?.client_id || !user?.user_email) return;

    setStudentLoading(true);
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}`;
        const res = await axios.get(url);
        const data = res.data?.data || res.data || null;
        setStudent(data && data.student_id ? data : null);
      } catch (err) {
        setStudent(null);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudent();
  }, [user?.client_id, user?.user_email, API_BASE_URL]);

  useEffect(() => {
    if (!student || !user?.client_id) return;
    if (student === null) {
      setLoading(false);
      setSchedules([]);
      setStudentResults([]);
      return;
    }

    const fetchSchedules = async () => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/api/studentresult/exam-schedules?college_id=${student.college_id}&client_id=${user.client_id}`;
        const res = await axios.get(url);
        setSchedules(
          (res.data?.data || []).map((s) => ({
            ...s,
            exam_type_name: s.exam_type?.exam_type_name,
          }))
        );

        const resultUrl = `${API_BASE_URL}/api/studentresult/student-final-results?student_id=${student.student_id}&client_id=${user.client_id}`;
        const resultRes = await axios.get(resultUrl);
        setStudentResults(resultRes.data?.data || []);
      } catch (err) {
        console.error("Error fetching schedules:", err);
        setSchedules([]);
        setStudentResults([]);
        showSnackbar("Error fetching exam schedules", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchSchedules();
  }, [student, user?.client_id, API_BASE_URL]);

  function handleShowResult(scheduleRow) {
    setSelectedResult(scheduleRow);
    setModalOpen(true);

    (async () => {
      try {
        const detail = scheduleRow.exam_schedule_details?.[0] || {};
        const url =
          `${API_BASE_URL}/api/studentresult/student-final-results?student_id=${student.student_id}` +
          `&exam_schedule_detail_id=${detail.exam_schedule_detail_id || ""}` +
          `&year_number=${detail.year_number || ""}` +
          `&semester_number=${detail.semester_number || ""}` +
          `&client_id=${user.client_id}`;
        const res = await axios.get(url);
        setResults(res.data?.data || []);

        const sgpaRow = res.data?.data?.find((r) => r.sgpa !== undefined && r.sgpa !== null);
        setSgpa(sgpaRow?.sgpa || null);
      } catch (err) {
        console.error("Error fetching results:", err);
        setResults([]);
        setSgpa(null);
        showSnackbar("Error fetching result details", "error");
      }
    })();
  }

  const handleDownloadPDF = async () => {
    if (!results || results.length === 0) {
      showSnackbar("No result data to download", "warning");
      return;
    }

    setDownloading(true);
    try {
      const detail = selectedResult?.exam_schedule_details?.[0] || {};
      const params = new URLSearchParams({
        student_id: results[0]?.student_id || student?.student_id,
        exam_schedule_detail_id: detail.exam_schedule_detail_id || "",
        year_number: detail.year_number || "",
        semester_number: detail.semester_number || "",
        client_id: user?.client_id,
      }).toString();

      window.open(`${API_BASE_URL}/api/studentresult/download-result-pdf?${params}`, "_blank");
      showSnackbar("PDF is being downloaded", "success");
    } catch (err) {
      console.error("Error downloading PDF:", err);
      showSnackbar("Error downloading PDF", "error");
    } finally {
      setDownloading(false);
    }
  };
  const getOrdinal = (num) => {
    const n = Number(num);
    if (!n) return { number: "", suffix: "" };

    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;

    let suffix = "th";

    if (lastTwoDigits < 11 || lastTwoDigits > 13) {
      if (lastDigit === 1) suffix = "st";
      else if (lastDigit === 2) suffix = "nd";
      else if (lastDigit === 3) suffix = "rd";
    }

    return { number: n, suffix };
  };

  // Get exam title for display
  const getExamTitle = () => {
    if (!selectedResult || !results.length) return "";

    const year =
      selectedResult.exam_schedule_details?.[0]?.year_number || selectedResult.year_number;

    const sem =
      selectedResult.exam_schedule_details?.[0]?.semester_number || selectedResult.semester_number;

    const examType = results[0]?.exam_type_name || "";

    const yearText = getOrdinal(year);
    const semText = getOrdinal(sem);

    return (
      <>
        {yearText.number}
        <sup>{yearText.suffix}</sup> YEAR {semText.number}
        <sup>{semText.suffix}</sup> SEMESTER {examType.toUpperCase()} EXAMINATIONS
      </>
    );
  };

  const rows = schedules.map((row, idx) => ({
    ...row,
    id: row.exam_schedule_master_id || idx,
  }));

  return (
    <Box p={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={1}
        sx={{
          width: "100%",
          maxWidth: 1100,
          margin: "0 auto",
          overflowX: "auto",
          boxSizing: "border-box",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            STUDENT RESULT
          </Typography>
        </Toolbar>
        <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2}>
            {studentLoading ? (
              <Typography variant="h6">Loading student info...</Typography>
            ) : student === null ? (
              <Typography variant="h6" color="error">
                Student not found.
              </Typography>
            ) : (
              <>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  autoHeight
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ width: "auto", minWidth: 900 }}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  <b>Note:</b> In case of any deviation, the concern students may contact the COE
                  (Examination Cell) immediately.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Modal for detailed result */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: 20,
            pb: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1976d2" }}>
                EXAM RESULTS
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {results && results.length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#1976d2",
                    textAlign: "center",
                  }}
                >
                  {getExamTitle()}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
              {results && results.length > 0 && (
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  sx={{
                    bgcolor: "#dc3545",
                    "&:hover": { bgcolor: "#c82333" },
                    fontWeight: 500,
                    textTransform: "none",
                    color: "#fff", // <-- This makes the text white
                  }}
                >
                  {downloading ? "Preparing PDF..." : "Download PDF"}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedResult && results.length > 0 && (
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 3, background: "#fff" }}>
              {/* Student Details Box - Updated layout without Program, Student ID moved to right */}
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  mb: 3,
                  p: 2.5,
                  background: "#f8f9fa",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, display: "flex", alignItems: "center" }}
                    >
                      <b style={{ minWidth: "110px" }}>Student Roll No:</b>{" "}
                      {results[0]?.student_rollno || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                      <b style={{ minWidth: "110px" }}>Student Name:</b>{" "}
                      {results[0]?.student_name || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, display: "flex", alignItems: "center" }}
                    >
                      <b style={{ minWidth: "110px" }}>Branch Name:</b>{" "}
                      {results[0]?.branch_name || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                      <b style={{ minWidth: "110px" }}>Student ID:</b>{" "}
                      {results[0]?.student_id || ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Results Table */}
              <DataGrid
                rows={results.map((r, index) => ({
                  ...r,
                  id: r.result_id || index,
                  subject_name: r.course_name,
                }))}
                columns={[
                  {
                    field: "sno",
                    headerName: "S.No",
                    width: 80,
                    valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
                  },
                  { field: "subject_name", headerName: "Course Name", flex: 1.5, minWidth: 200 },
                  {
                    field: "internal_marks",
                    headerName: "Internal",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "external_marks",
                    headerName: "External",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "total_marks",
                    headerName: "Total",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "credits",
                    headerName: "Credits",
                    width: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade",
                    headerName: "Grade",
                    width: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade_point",
                    headerName: "Grade Points",
                    width: 110,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <span>
                        {params.value !== undefined && params.value !== null
                          ? Number(params.value).toFixed(0)
                          : "-"}
                      </span>
                    ),
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    width: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <span
                        style={{
                          color:
                            params.value === "PASS"
                              ? "#2e7d32"
                              : params.value === "FAIL"
                              ? "#d32f2f"
                              : "#000",
                          fontWeight: 600,
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor:
                            params.value === "PASS"
                              ? "#e8f5e9"
                              : params.value === "FAIL"
                              ? "#ffebee"
                              : "transparent",
                        }}
                      >
                        {params.value || "-"}
                      </span>
                    ),
                  },
                ]}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  background: "#fff",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                  },
                }}
              />

              {/* Summary Box */}
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  mt: 3,
                  p: 2.5,
                  background: "#f8f9fa",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                      <b style={{ minWidth: "80px" }}>Result:</b>
                      <span
                        style={{
                          color: results[0]?.sgpa && results[0]?.sgpa != 0 ? "#2e7d32" : "#d32f2f",
                          fontWeight: 600,
                        }}
                      >
                        {results[0]?.exam_type_name === "REGULAR" &&
                        results[0]?.sgpa &&
                        results[0]?.sgpa != 0
                          ? "PASS"
                          : ""}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                      <b style={{ minWidth: "80px" }}>Percentage:</b>
                      {results[0]?.percentage ? Number(results[0].percentage).toFixed(2) + "%" : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                      <b style={{ minWidth: "80px" }}>SGPA:</b>
                      <span style={{ fontWeight: 600, color: "#1976d2" }}>
                        {sgpa || results[0]?.overall_sgpa || ""}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() => setModalOpen(false)}
            color="error"
            variant="contained"
            size="medium"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StudentResult;
